/* ==============================================================
   WHICH PARALLEL PETITION A TICKET POINTS AT — one reading, for the
   server (through server/generated/parallelRef.generated.ts, copied by
   scripts/gen-server-roster.mjs) and for the browser (@shared/parallelRef).

   HubSpot carries the petition twice on a ticket: «Parallel ID»
   (`parallel_id`, the bare id the portal asks Parallel for) and
   «Parallel ID Link» (`parallelid`, the link people open). Each side used
   to read them its own way, and both trusted whatever was written. On the
   five boards of 13 Oct 2026, of the 2,430 tickets that carry either:
   185 links «…/petitions//replies» with no petition in them, 8 ids that
   were a whole link (the portal then built «…/petitions/https://…»),
   8 ids «0» and 17 «NA» / «none» / «N/A» sent to Parallel as if they
   were petitions, and 196 tickets whose link opens ANOTHER petition than
   the id (90 of them in Portugal) — the card's documents came from one,
   the header's «Open in Parallel» from the other, and nothing said so
   (Portugal, 9 Oct 2026: «some Parallels aren't connecting automatically»).
   In each Portuguese one the id is the LATER petition in Parallel's own
   numbering: a second petition was sent and only «Parallel ID» followed.

   The rule, the same everywhere:
     · the id is whatever petition id the field holds — a bare id, a link,
       a link pasted into the id field, a link inside a link;
       «0», «NA», «none», an empty link… are no petition at all;
     · the «Parallel ID» property wins; the link only fills in when there
       is no id;
     · the link the portal opens always opens THAT petition — the one
       written on the ticket when it is a clean link to it, else one built
       from the id;
     · when both properties name a petition and they differ, the one in
       the link is handed back too (`parallelLinkId`), so the case can say
       so instead of choosing in silence.
   ============================================================== */

export const PARALLEL_PETITIONS = 'https://www.onparallel.com/app/petitions/';

/** A petition id: letters and digits, never a placeholder. Parallel's are
 *  19–21 characters; anything under 10 is «0», «NA», «none»… */
const PETITION_ID = /^[A-Za-z0-9]{10,}$/;

/** The petition id inside whatever a HubSpot field holds, or null. */
export function petitionIdOf(raw: unknown): string | null {
  const s = String(raw ?? '').trim();
  if (!s) return null;
  /* The LAST «petitions/»: a link glued inside a link
     («…/petitions/https://…/petitions/<id>/replies») names the inner one. */
  const at = s.lastIndexOf('petitions/');
  if (at < 0 && s.includes('://')) return null; // a link, but not to a petition
  const tail = at < 0 ? s : s.slice(at + 'petitions/'.length);
  const id = tail.match(/^[A-Za-z0-9]+/)?.[0] || '';
  return PETITION_ID.test(id) ? id : null;
}

/** The petition's page in Parallel, from its id. */
export function petitionLink(id: string): string {
  return `${PARALLEL_PETITIONS}${id}/replies`;
}

/** A link worth keeping exactly as written: ONE petition link on Parallel's
 *  own site (a language prefix, a «?comments=…» are fine), nothing glued. */
const CLEAN_LINK = /^https:\/\/[a-z0-9.-]*onparallel\.com\/(?:[a-z]{2}\/)?app\/petitions\/([A-Za-z0-9]+)(?:\/[a-z-]*)?\/?(?:[?#]\S*)?$/i;

export interface ParallelRef {
  /** The petition the portal reads: «Parallel ID» first, else the link's. */
  parallelId: string | null;
  /** The link the portal opens — always to `parallelId`'s petition. */
  parallelUrl: string | null;
  /** Only when «Parallel ID Link» names ANOTHER petition than «Parallel ID»:
   *  the link's petition. Null when they agree or one of them is missing. */
  parallelLinkId: string | null;
}

/** Both HubSpot properties of a ticket → the one petition the portal uses. */
export function resolveParallel(idField: unknown, linkField: unknown): ParallelRef {
  const fromId = petitionIdOf(idField);
  const fromLink = petitionIdOf(linkField);
  const parallelId = fromId || fromLink;
  if (!parallelId) return { parallelId: null, parallelUrl: null, parallelLinkId: null };
  const link = String(linkField ?? '').trim();
  const clean = link.match(CLEAN_LINK);
  return {
    parallelId,
    parallelUrl: clean && clean[1] === parallelId ? link : petitionLink(parallelId),
    parallelLinkId: fromId && fromLink && fromLink !== fromId ? fromLink : null,
  };
}
